import { createStore } from 'jotai';
import { DashboardType } from 'views/Admin/Dashboard/types';
import {
  companyIdAtom,
  companyLogoAtom,
  currentCompanyAtom,
  currentDashboardAtom,
} from 'views/state';

/**
 * Company-switch bridge: lets LogoDropdown (deep in the tree) request a full
 * Jotai store reset and hand the new company's identity to the fresh store.
 *
 * Jotai v2 has no "reset all atoms" API, so cross-company isolation is enforced
 * structurally: JotaiStoreProvider remounts <Provider> with a fresh createStore()
 * on every reset. LogoDropdown cannot reach that root state directly, so it
 * dispatches through this module.
 *
 * IMPORTANT: the module is feature-specific (company switch) and imports the
 * shared identity atoms, but it never references a specific store instance or
 * React context — so it safely outlives Provider remounts.
 */

export interface StoreIdentity {
  id: number;
  name: string;
  logo: string;
  dashboardType: DashboardType;
}

type Listener = (identity: StoreIdentity) => void;
const listeners = new Set<Listener>();

/**
 * Subscribe to store-reset requests (triggered by company switch).
 * Returns an unsubscribe function — call it on effect cleanup.
 */
export function subscribeStoreReset(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Request a full store reset now (user switched companies). Fires all
 * subscribers synchronously with the identity the fresh store should be seeded
 * with. Only called from the user `updateCompany` handler in LogoDropdown —
 * never from the mount/effect path.
 */
export function requestStoreReset(identity: StoreIdentity): void {
  listeners.forEach((listener) => listener(identity));
}

// Scratch store reused for persistence writes (see persistIdentity). A shared
// module-scope store avoids allocating one per switch; `store.set` does not
// trigger atomWithStorage's onMount subscription, so no listeners accumulate.
const scratchStore = createStore();

/**
 * Persist the new identity to localStorage WITHOUT writing to the currently
 * mounted store — a normal atom write on the old store would re-derive its data
 * atoms (dependency on companyId) and fire a duplicate dashboard request before
 * the swap. The scratch store reuses the canonical atomWithStorage write path.
 *
 * Must run BEFORE the store swap: callApi keys every request with the company
 * id it reads from localStorage (callApi.ts) at request time, so the fresh
 * store's first requests need the new id already persisted.
 */
export function persistIdentity(id: number, name: string, logo: string): void {
  scratchStore.set(currentCompanyAtom, name);
  scratchStore.set(companyLogoAtom, logo);
  scratchStore.set(companyIdAtom, id);
}

/**
 * Seed a freshly-created store with the NEW company identity BEFORE its first
 * commit. `getOnInit` only snapshots localStorage at module load, so without
 * this seeding a brand-new store (created on company switch) would render a
 * stale -1 / default-CA first frame and fire spurious `-1`/CA requests before
 * atomWithStorage's onMount rehydrates real values. The values come from the
 * switch site (never read back from localStorage), so the first frame always
 * carries the actual new identity.
 */
export function seedStoreIdentity(
  store: ReturnType<typeof createStore>,
  identity: StoreIdentity,
): void {
  store.set(companyIdAtom, identity.id);
  store.set(currentCompanyAtom, identity.name);
  store.set(companyLogoAtom, identity.logo);
  store.set(currentDashboardAtom, identity.dashboardType);
}
